import {
  useEditMyDefaultLanguageMutation,
  useEditMyPasswordMutation,
  useGetPartnerQuery,
} from '@/api/api.slice.tsx';
import { useAppSelector } from '@/app/hooks.ts';
import { getOnBoardingState } from '@/features/OnBoarding/OnBoarding.slice.ts';
import PublicIcon from '@mui/icons-material/Public';
import { Box, InputAdornment, MenuItem, Stack } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import i18n, { availableLocales } from '@/i18n/i18n.ts';
import {
  Alert,
  Button,
  getMessageFromError,
  isPasswordStrongEnough,
  Loader,
  PossibleLocalesType,
  renderPasswordHelperText,
  Select,
  TextFieldPassword,
  Typography,
} from '@arsnovasystems/ars-front-lib';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

interface IFormInput {
  password: string;
  passwordBis: string;
}

/**
 * Step 1 - Presentation, Language and password
 * component for Onboarding
 *
 * @returns JSX of the component
 */
export const StepPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: me, error } = useGetPartnerQuery();

  const [updatePasswordMode, setUpdatePasswordMode] = useState(false);

  const { onBoardingMode } = useAppSelector(getOnBoardingState);
  const [savePartnerPassword, savePartnerPasswordResult] =
    useEditMyPasswordMutation();
  const [editMyDefaultLanguage, editMyDefaultLanguageResult] =
    useEditMyDefaultLanguageMutation();

  const { control, handleSubmit, formState, trigger, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      password: '',
      passwordBis: '',
    },
  });

  const watchedPassword = watch('password');
  const watchedPasswordBis = watch('passwordBis');

  // effect watching on password
  useEffect(() => {
    if (formState.isDirty && watchedPasswordBis !== '') {
      void trigger('passwordBis'); // retriggers validation
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, watchedPassword, watchedPasswordBis]);

  if (!me) {
    return <Loader />;
  }

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    const { error } = await savePartnerPassword({
      password: data.password,
      noEmail: true,
    });
    if (error === undefined) {
      // updating the Symfony "Authentication" will disconnect the user
      // so : reload page but KEEP the TOKEN in URL!
      const search = location.search;
      window.location.href = `/onboarding/step/account${search}`;
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          margin: 2,
        }}
      >
        <Select<PossibleLocalesType>
          sx={{
            minWidth: '200px',
          }}
          label={t('ik.select.your.language')}
          value={i18n.language as PossibleLocalesType}
          onChange={(e) => {
            const val = e.target.value;
            void i18n.changeLanguage(val);
            void editMyDefaultLanguage(val);
          }}
          startAdornment={
            <InputAdornment position='start'>
              <PublicIcon />
            </InputAdornment>
          }
        >
          {availableLocales.map((locale) => (
            <MenuItem
              key={locale}
              value={locale}
            >
              {locale}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Stack
        sx={{
          maxWidth: '1200px',
          justifyContent: 'space-between',
          alignItems: 'space-between',
        }}
      >
        <Typography
          variant='body1'
          sx={{
            marginTop: 2,
          }}
        >
          {onBoardingMode === 'invited' ?
            t('ik.onboarding.stepOne.welcomeMsg')
          : t('ik.onboarding.migrate.stepOne.welcomeMsg')}
        </Typography>

        <Typography
          variant='body1'
          sx={{
            marginTop: 4,
          }}
        >
          {`${t('ik.onboarding.stepOne.language.hints')}${!me.hasPassword ? t('ik.onboarding.stepOne.password.hints') : ''}${t('ik.onboarding.stepOne.next.hints')}`}
        </Typography>

        {me.hasPassword && (
          <Stack sx={{ gap: 1 }}>
            <Typography>
              {t('ik.onboarding.stepOne.passwordKeepSame.description')}
            </Typography>
            <Button
              variant='outlined'
              sx={{
                width: 'fit-content',
              }}
              onClick={() => {
                setUpdatePasswordMode(!updatePasswordMode);
              }}
            >
              {!updatePasswordMode ?
                t('ik.onboarding.stepOne.passwordChange.button')
              : t('ik.onboarding.stepOne.passwordKeepSame.button')}
            </Button>
          </Stack>
        )}

        {!me.hasPassword || updatePasswordMode ?
          <form onSubmit={void handleSubmit(onSubmit)}>
            <Box sx={{ maxWidth: 400 }}>
              <Controller
                name={'password'}
                control={control}
                rules={{
                  required: true,
                  validate: {
                    hasAnyError: isPasswordStrongEnough,
                  },
                }}
                render={({ field, fieldState }) => {
                  return (
                    <Fragment>
                      <TextFieldPassword
                        {...field}
                        fullWidth={true}
                        error={fieldState.invalid}
                        placeholder={t('Your new password')}
                        sx={{ mt: 5 }}
                        label={t('Password')}
                      />

                      {renderPasswordHelperText({
                        fieldValue: field.value,
                        isDirty: fieldState.isDirty,
                        hasError: fieldState.invalid,
                      })}
                    </Fragment>
                  );
                }}
              />

              <Controller
                name={'passwordBis'}
                control={control}
                rules={{
                  required: true,
                  validate: {
                    isMatchingFirstPassword: (value, formValues) => {
                      if (formValues.password !== value) {
                        return t('The two passwords are not identical');
                      }
                      return formValues.password === value;
                    },
                  },
                }}
                render={({ field, fieldState }) => {
                  return (
                    <Fragment>
                      <TextFieldPassword
                        {...field}
                        label={t('Retype the same password')}
                        placeholder={t('Type your new password again')}
                        fullWidth={true}
                        error={fieldState.invalid}
                        sx={{
                          marginTop: 3,
                        }}
                      />
                      <Box
                        sx={{
                          marginTop: '2px',
                          marginLeft: '1px',
                        }}
                      >
                        {fieldState.invalid ?
                          <Typography
                            variant={'subtitle2'}
                            color={'error'}
                          >
                            {fieldState.error?.message}
                          </Typography>
                        : null}
                        {fieldState.isDirty && !fieldState.invalid ?
                          <Typography
                            variant={'subtitle2'}
                            color={'success'}
                          >
                            {t('The two passwords are matching ! 🤝')}
                          </Typography>
                        : null}
                      </Box>
                    </Fragment>
                  );
                }}
              />
            </Box>

            {editMyDefaultLanguageResult.error && (
              <Alert>
                {getMessageFromError(editMyDefaultLanguageResult.error)}
              </Alert>
            )}

            {error && <Alert>{getMessageFromError(error)}</Alert>}
            {savePartnerPasswordResult.error && (
              <Alert>
                {getMessageFromError(savePartnerPasswordResult.error)}
              </Alert>
            )}
          </form>
        : null}
        <Stack
          direction={'row'}
          sx={{ justifyContent: 'center', gap: 3, marginTop: 3 }}
        >
          <Button
            size={'large'}
            variant={'outlined'}
            disabled
          >
            {t('Back')}
          </Button>
          <Button
            loading={savePartnerPasswordResult.isLoading}
            disabled={
              (!me.hasPassword && !formState.isValid) ||
              (updatePasswordMode && !formState.isValid)
            }
            size={'large'}
            type={'submit'}
            onClick={() => {
              const skipPassword = me.hasPassword && !updatePasswordMode;
              if (skipPassword) {
                void navigate(`/onboarding/step/account${location.search}`);
                return;
              } else {
                void handleSubmit(onSubmit)();
              }
            }}
          >
            {t('Next')}
          </Button>
        </Stack>
      </Stack>
    </>
  );
};
